import { create } from 'zustand';
import type { SessionStateValue } from '@/api/types';

/**
 * 系统全局状态 store — 由 WebSocket `system` 频道事件驱动 + 轮询 dashboard 兜底。
 *
 * 交易锁定(tradeLocked) / 紧急停机(emergencyStop) 都是布尔门,
 * 任一为 true 时,App 顶部展示告示徽章 + 全局拦截写操作。
 */
interface LiquidationProgress {
  active: boolean;
  // N-2:v2 单真实账户口径——已平 / 失败真实持仓笔数(不再有雇员腿)。
  closed?: number;
  failed?: number;
  total?: number;
  triggeredBy?: 'SCHEDULED' | 'EMERGENCY' | 'MANUAL';
  emergencyStopHeld?: boolean;
}

interface SystemState {
  sessionState: SessionStateValue;
  stateSince: string | null;

  tradeLocked: boolean;
  unlockAt: string | null;

  emergencyStop: boolean;
  emergencyReason: string | null;
  emergencyTriggeredBy: 'AUTO_RISK' | 'MANUAL_ADMIN' | null;
  /** 最近一次由 WS / 乐观更新写 emergencyStop 的时刻(ms)。轮询兜底据此设宽限,避免在途旧轮询误清新停机 */
  lastEmergencyWsAt: number;

  liquidation: LiquidationProgress;

  setSessionState: (s: SessionStateValue, since?: string) => void;
  setTradeLock: (locked: boolean, unlockAt?: string | null) => void;
  setEmergencyStop: (on: boolean, reason?: string, triggeredBy?: 'AUTO_RISK' | 'MANUAL_ADMIN') => void;
  setLiquidation: (next: Partial<LiquidationProgress>) => void;
  hydrateFromDashboard: (
    s: SessionStateValue,
    tradeLocked: boolean,
    unlockAt: string | undefined,
    since: string | undefined,
    emergency?: { active: boolean; reason?: string | null },
  ) => void;
}

/** 轮询兜底纠正 emergencyStop 前,对最近一次 WS/乐观写入的宽限窗(ms)。
 *  防「新停机刚由 WS 置起 → 一个更早发出、返回 active=false 的在途轮询把它清掉」的抖动。 */
const EMERGENCY_POLL_GRACE_MS = 8_000;

export const useSystemStore = create<SystemState>()((set) => ({
  sessionState: 'OPEN',
  stateSince: null,

  tradeLocked: false,
  unlockAt: null,

  emergencyStop: false,
  emergencyReason: null,
  emergencyTriggeredBy: null,
  lastEmergencyWsAt: 0,

  liquidation: { active: false },

  setSessionState: (s, since) => set({ sessionState: s, stateSince: since ?? null }),
  setTradeLock: (locked, unlockAt) => set({ tradeLocked: locked, unlockAt: unlockAt ?? null }),
  setEmergencyStop: (on, reason, triggeredBy) =>
    set({
      emergencyStop: on,
      emergencyReason: on ? reason ?? null : null,
      emergencyTriggeredBy: on ? triggeredBy ?? null : null,
      lastEmergencyWsAt: Date.now(),
    }),
  setLiquidation: (next) => set((state) => ({ liquidation: { ...state.liquidation, ...next } })),
  hydrateFromDashboard: (s, tradeLocked, unlockAt, since, emergency) =>
    set((state) => {
      const base = {
        sessionState: s,
        stateSince: since ?? null,
        tradeLocked,
        unlockAt: unlockAt ?? null,
      };
      // 后端未带 emergencyStop(灰度中/旧后端)→ 不动本地(仍由 WS 事件驱动)。
      if (!emergency) return base;
      // 兜底对账:WS 事件在网络抖动里丢失时,这里据服务端真实标志 5s 内校正横幅。
      if (emergency.active) {
        // 置起。保留 WS 已带的更完整 reason(带触发方文案),仅在本地无 reason 时用轮询 reason。
        return {
          ...base,
          emergencyStop: true,
          emergencyReason: state.emergencyReason ?? emergency.reason ?? null,
        };
      }
      // 服务端已解除。宽限窗内刚有 WS/乐观写入 → 可能是「新停机 vs 在途旧轮询」竞态,不清,留待下轮。
      if (state.emergencyStop && Date.now() - state.lastEmergencyWsAt < EMERGENCY_POLL_GRACE_MS) {
        return base;
      }
      return { ...base, emergencyStop: false, emergencyReason: null, emergencyTriggeredBy: null };
    }),
}));
